import React, { useEffect, useState } from 'react';
import GradientButton from '../../buttons/GradientButton';
import '../pages/payment-subsettings/PaymentSubsettings.css';

const ServiceTaxSettings = ({ service, onSave }) => {
  const [taxSettings, setTaxSettings] = useState({
    isEnabled: false,
    serviceRate: 0,
    description: '',
    lastUpdated: new Date().toISOString(),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const s = service?.taxSettings || {};
    setTaxSettings({
      isEnabled: s.isEnabled || false,
      serviceRate: s.serviceRate ?? 0,
      description: s.description || '',
      lastUpdated: s.lastUpdated || new Date().toISOString(),
    });
  }, [service?.id, service?.taxSettings]);

  const handleChange = (field, value) => {
    setTaxSettings((prev) => ({
      ...prev,
      [field]: value,
      lastUpdated: new Date().toISOString(),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave?.(taxSettings);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ps-root ps-root--embedded">
      <div className="ps-panel">
        <p className="ps-panel__title">Service tax</p>
        <p>
          Override tax for this service only. When disabled, no tax is added at checkout unless you
          configure tax through your payment processor.
        </p>
      </div>

      <div className="ps-option-with-help">
        <label className="ps-option-box" htmlFor="service-tax-enabled">
          <input
            id="service-tax-enabled"
            type="checkbox"
            className="ps-checkbox"
            checked={taxSettings.isEnabled}
            onChange={(e) => handleChange('isEnabled', e.target.checked)}
          />
          <span className="ps-option-box__body">
            <span className="ps-option-box__title">Use custom tax for this service</span>
            <p className="ps-option-box__hint">
              Tick the box to apply a specific tax rate to this service&apos;s price (including add-ons at booking).
            </p>
          </span>
        </label>
        <button
          type="button"
          className="ps-help-btn"
          title="This rate applies to the total service price shown at checkout."
          aria-label="Help: custom tax for this service"
        >
          ?
        </button>
      </div>

      {taxSettings.isEnabled && (
        <>
          <h4 className="ps-section-head">
            Tax rate
            <button
              type="button"
              className="ps-help-btn"
              title="Applied to the total service price including add-ons selected at booking."
              aria-label="Help: tax rate"
            >
              ?
            </button>
          </h4>

          <div className="ps-form-grid">
            <div className="ps-field">
              <label htmlFor="service-tax-rate">Service tax rate</label>
              <div className="ps-input-wrap">
                <input
                  id="service-tax-rate"
                  type="number"
                  className="ps-input"
                  min={0}
                  max={100}
                  step={0.01}
                  value={taxSettings.serviceRate}
                  onChange={(e) => handleChange('serviceRate', Number(e.target.value))}
                />
                <span className="ps-input-suffix">%</span>
              </div>
              <p className="ps-field-hint">Percentage added to the service total at checkout</p>
            </div>
          </div>

          <div className="ps-field" style={{ marginBottom: '1rem' }}>
            <label htmlFor="service-tax-description">Tax description (optional)</label>
            <textarea
              id="service-tax-description"
              className="ps-textarea"
              rows={3}
              value={taxSettings.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="e.g. Includes state and local sales tax"
            />
            <p className="ps-field-hint">Shown internally or on receipts where tax line items are displayed</p>
          </div>

          <div className="ps-actions ps-actions--flush">
            <GradientButton variant="primary" className="ps-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <span className="ps-btn-spinner" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </GradientButton>
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceTaxSettings;
